import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Shield, Loader2, CheckCircle } from 'lucide-react';

const SetupAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [setupNeeded, setSetupNeeded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    voornaam: '',
    van: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    checkSetupNeeded();
  }, []);

  const checkSetupNeeded = async () => {
    try {
      const response = await axios.get(`${API}/setup-admin/check`);
      setSetupNeeded(response.data.setup_needed);
      
      if (!response.data.setup_needed) {
        toast.info('Admin reeds opgestel. Gaan na aanmelding.');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error) {
      toast.error('Kon nie opstelling status nagaan nie');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.voornaam || !formData.van || !formData.email || !formData.password) {
      toast.error('Alle velde is vereist');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Wagwoord moet ten minste 8 karakters lank wees');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Wagwoorde stem nie ooreen nie');
      return;
    }

    setSubmitting(true);

    try {
      const formPayload = new FormData();
      formPayload.append('voornaam', formData.voornaam);
      formPayload.append('van', formData.van);
      formPayload.append('email', formData.email);
      formPayload.append('password', formData.password);

      await axios.post(`${API}/setup-admin`, formPayload);
      
      toast.success('Hoofadmin suksesvol geskep! Gaan na aanmelding...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie admin skep nie');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!setupNeeded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-lg font-semibold">Admin Reeds Opgestel</p>
            <p className="text-sm text-gray-600 mt-2">Gaan na aanmelding...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-2 border-orange-200 shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-orange-500 p-3 rounded-full">
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            DIE BIBLIOTEEK
          </CardTitle>
          <CardDescription className="text-base mt-2">
            Eenmalige Hoofadmin Opstelling
          </CardDescription>
          <p className="text-sm text-gray-600 mt-2">
            Skep die eerste hoofadmin rekening vir die stelsel
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="voornaam">Voornaam *</Label>
              <Input
                id="voornaam"
                value={formData.voornaam}
                onChange={(e) => setFormData({ ...formData, voornaam: e.target.value })}
                placeholder="Voornaam"
                required
              />
            </div>

            <div>
              <Label htmlFor="van">Van *</Label>
              <Input
                id="van"
                value={formData.van}
                onChange={(e) => setFormData({ ...formData, van: e.target.value })}
                placeholder="Van"
                required
              />
            </div>

            <div>
              <Label htmlFor="email">E-pos *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="jou@email.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="password">Wagwoord *</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Minimum 8 karakters"
                required
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword">Bevestig Wagwoord *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Herhaal wagwoord"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Besig...
                </>
              ) : (
                <>
                  <Shield className="mr-2 h-4 w-4" />
                  Skep Hoofadmin
                </>
              )}
            </Button>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded text-sm">
              <p className="text-blue-800">
                <strong>Belangrik:</strong> Hierdie funksie werk slegs een keer. 
                Na hierdie rekening geskep is, sal dit nie weer beskikbaar wees nie.
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetupAdmin;
